import { FormattedMessage, useIntl } from 'react-intl';

import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { Nullable } from '~/types/Nullable';

import { ModelProgress } from '../types/model';
import { mapBoostStateToDisplay, mapPublishStatusToDisplay } from './utils';

interface Props {
  progress: ModelProgress;
  datePublished: Nullable<string>;
}

export const PublishProgressMetrics = ({ progress, datePublished }: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();

  return (
    <Layout direction="column" flex="1 0 auto">
      <Layout spacing={1}>
        <Text>
          <FormattedMessage
            id="CustomAiDebugModel.modelPublishStatusLabel"
            defaultMessage="Publish status:"
            description="Label text for the model publish status"
          />
        </Text>
        <Text variant="body-bold">
          {mapPublishStatusToDisplay(progress.publishStatus, intl)}
        </Text>
      </Layout>
      <Layout spacing={1}>
        <Text>
          <FormattedMessage
            id="CustomAiDebugModel.modelPublishedDateLabel"
            defaultMessage="Date published:"
            description="Label text indicating the date the model was published"
          />
        </Text>
        <Text variant="body-bold">
          {datePublished
            ? formatDate(datePublished, 'full_datetime')
            : intl.formatMessage({
                id: 'CustomAiDebugModel.modelPublishedDateNotApplicable',
                defaultMessage: '—',
                description:
                  'Display text for the published date indicating that the model was not published',
              })}
        </Text>
      </Layout>
      <Layout spacing={1}>
        <Text>
          <FormattedMessage
            id="CustomAiDebugModel.modelDocsInScopeLabel"
            defaultMessage="Documents in scope:"
            description="Label text for the number of documents in scope"
          />
        </Text>
        <Text variant="body-bold">{formatNumber(progress.docsInScope)}</Text>
      </Layout>
      <Layout spacing={1}>
        <Text>
          <FormattedMessage
            id="CustomAiDebugModel.modelDocsProcessedLabel"
            defaultMessage="Documents processed:"
            description="Label text for the number of documents already processed"
          />
        </Text>
        <Text variant="body-bold">{formatNumber(progress.docsProcessed)}</Text>
      </Layout>
      <Layout spacing={1}>
        <Layout>
          <Text>
            <FormattedMessage
              id="CustomAiDebugModel.modelDocsInScopeProcessingLabel"
              defaultMessage="Documents in scope have changed?"
              description="Label text for whether or not the model scope has changed"
            />
          </Text>
          <Icon
            icon="info"
            tooltip={intl.formatMessage({
              id: 'CustomAiDebugModel.modelDocsInScopeProcessingInfo',
              defaultMessage:
                'e.g. Have documents that were part of the original scope been deleted since the model was published?',
              description:
                'Hint text with an example of how the scope might have changed',
            })}
          />
        </Layout>
        <Text variant="body-bold">
          {progress.docsInScopeProcessing
            ? intl.formatMessage({
                id: 'CustomAiDebugModel.docsProcessingYes',
                defaultMessage: 'Yes',
                description:
                  'Yes, the documents in scope have changed since the model was published',
              })
            : intl.formatMessage({
                id: 'CustomAiDebugModel.docsProcessingNo',
                defaultMessage: 'No',
                description: 'No, the documents in scope have not changed',
              })}
        </Text>
      </Layout>
      <Layout spacing={1}>
        <Text>
          <FormattedMessage
            id="CustomAiDebugModel.modelDocsFailedLabel"
            defaultMessage="Documents failed:"
            description="Label text for the number of documents that failed to process"
          />
        </Text>
        <Text variant="body-bold">{formatNumber(progress.docsFailed)}</Text>
      </Layout>
      <Layout spacing={1}>
        <Text>
          <FormattedMessage
            id="CustomAiDebugModel.modelValuesFoundLabel"
            defaultMessage="Values found:"
            description="Label text for the number of values the model found"
          />
        </Text>
        <Text variant="body-bold">{formatNumber(progress.valuesFound)}</Text>
      </Layout>
      <Layout spacing={1}>
        <Text>
          <FormattedMessage
            id="CustomAiDebugModel.modelBoostStateLabel"
            defaultMessage="Boost status:"
            description="Label text indicating the state of the boost process for the model"
          />
        </Text>
        <Text variant="body-bold">
          {mapBoostStateToDisplay(progress.optimizeState, intl)}
        </Text>
      </Layout>
    </Layout>
  );
};
